import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';

const AddParticipantModal = ({ show, onHide, users, eventRoles, onSave, currentUser, eventDate, participants }) => {
    const [formData, setFormData] = useState({
        user_id: '',
        role_id: '',
    });
    const [isUserUnavailable, setIsUserUnavailable] = useState(false);
    const [unavailableMessage, setUnavailableMessage] = useState('');
    const [isDuplicate, setIsDuplicate] = useState(false);
    const [duplicateMessage, setDuplicateMessage] = useState('');

    // Reset state when modal is opened
    useEffect(() => {
        if (show) {
            setFormData({ user_id: '', role_id: '' });
            setIsUserUnavailable(false);
            setUnavailableMessage('');
            setIsDuplicate(false);
            setDuplicateMessage('');
        }
    }, [show]);

    // Filter users based on selected role
    const filteredUsers = useMemo(() => {
        if (!formData.role_id) return [];
        const roleId = parseInt(formData.role_id);
        // Find users who have the selected role
        return users.filter(u => u.roles && u.roles.some(r => r.id === roleId));
    }, [formData.role_id, users]);

    // Handle role change
    const handleRoleChange = (e) => {
        // Reset user selection when role changes
        setFormData({ user_id: '', role_id: e.target.value });
    };

    // Check for user unavailability
    useEffect(() => {
        if (formData.user_id && eventDate) {
            const selectedUser = users.find(u => u.id === parseInt(formData.user_id));
            if (selectedUser && selectedUser.unavailability_periods) {
                const eventDateObj = new Date(eventDate);
                eventDateObj.setHours(0, 0, 0, 0);
                const unavailable = selectedUser.unavailability_periods.some(period => {
                    const startDate = new Date(period.start_date);
                    startDate.setHours(0, 0, 0, 0);
                    const endDate = new Date(period.end_date);
                    endDate.setHours(0, 0, 0, 0);
                    return eventDateObj >= startDate && eventDateObj <= endDate;
                });
                setIsUserUnavailable(unavailable);
                if (unavailable) {
                    setUnavailableMessage(`Выбранный пользователь недоступен на дату события (${eventDateObj.toLocaleDateString()}).`);
                } else {
                    setUnavailableMessage('');
                }
            }
        } else {
            setIsUserUnavailable(false);
            setUnavailableMessage('');
        }
    }, [formData.user_id, users, eventDate]);

    // Check for duplicate participant
    useEffect(() => {
        if (formData.user_id && formData.role_id && participants) {
            const duplicate = participants.some(
                p => p.user.id === parseInt(formData.user_id) && p.role.id === parseInt(formData.role_id)
            );
            setIsDuplicate(duplicate);
            if (duplicate) {
                const roleName = eventRoles.find(r => r.id === parseInt(formData.role_id))?.name || 'выбранной роли';
                setDuplicateMessage(`Этот пользователь уже добавлен в команду с ролью "${roleName}".`);
            } else {
                setDuplicateMessage('');
            }
        }
    }, [formData.user_id, formData.role_id, participants, eventRoles]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (isDuplicate || isUserUnavailable) return;
        onSave(formData);
    };

    // Group event roles by department for the dropdown
    const groupedRoles = useMemo(() => {
        return eventRoles.reduce((acc, role) => {
            const departmentName = role.department.name || 'Без направления';
            if (!acc[departmentName]) {
                acc[departmentName] = [];
            }
            acc[departmentName].push(role);
            return acc;
        }, {});
    }, [eventRoles]);

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>Добавить участника в команду</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {isUserUnavailable && <Alert variant="warning">{unavailableMessage}</Alert>}
                {isDuplicate && <Alert variant="danger">{duplicateMessage}</Alert>}
                <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                        <Form.Label>1. Выберите роль</Form.Label>
                        <Form.Select
                            name="role_id"
                            value={formData.role_id}
                            onChange={handleRoleChange}
                            required
                        >
                            <option value="">Сначала выберите роль</option>
                            {Object.entries(groupedRoles).map(([deptName, roles]) => (
                                <optgroup label={deptName} key={deptName}>
                                    {roles.map(r => (
                                        <option key={r.id} value={r.id}>{r.name}</option>
                                    ))}
                                </optgroup>
                            ))}
                        </Form.Select>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label>2. Выберите пользователя</Form.Label>
                        <Form.Select
                            name="user_id"
                            value={formData.user_id}
                            onChange={handleChange}
                            required
                            disabled={!formData.role_id}
                        >
                            <option value="">Выберите пользователя</option>
                            {filteredUsers.map(u => (
                                <option key={u.id} value={u.id}>{u.id === currentUser.id ? `${u.name || u.email} (Вы)` : (u.name || u.email)}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>

                    <Button variant="primary" type="submit" className="w-100" disabled={isUserUnavailable || isDuplicate || !formData.user_id || !formData.role_id}>
                        Добавить
                    </Button>
                </Form>
            </Modal.Body>
        </Modal>
    );
};

export default AddParticipantModal;
